/**
 * ─────────────────────────────────────────────────────────────
 *  DESIGN SYSTEM  —  single source of truth
 *
 *  Hierarchy:
 *    typeScale  → font sizes used inline in page/component JSX
 *    uiScale    → sizes used in CSS classes (nav, buttons, etc.)
 *    injectCSSVars() → writes both scales + colours as CSS custom
 *                      properties so index.css can consume them
 *    getBrand() → returns all per-brand tokens (colours, radii,
 *                 shadows, spacing, …) for inline JSX styles
 *
 *  To resize text globally → edit typeScale / uiScale here.
 *  Everything else updates automatically.
 * ─────────────────────────────────────────────────────────────
 */

// ── TYPE SCALE ───────────────────────────────────────────────
// Inline font sizes used throughout page & component JSX.
// Update once here → all components that reference b.fz.* update.
export const typeScale = {
  xs:   '0.82rem',   // badges, captions, date-meta
  sm:   '0.93rem',   // feature bullets, secondary text
  base: '1rem',      // body paragraphs, card descriptions
  lg:   '1.18rem',   // card h3 headings
  xl:   '1.35rem',   // larger sub-headings
};

// ── UI SCALE ─────────────────────────────────────────────────
// Sizes for CSS-class-controlled elements: nav, buttons, footer.
// These are written as CSS custom properties by injectCSSVars().
export const uiScale = {
  nav:          '16px',    // top-level nav links
  dropdown:     '15px',    // dropdown menu items
  btn:          '15px',    // primary CTA buttons
  btnMwo:       '14px',    // MWO outlined button (condensed font)
  eyebrow:      '12px',    // eyebrow / section label
  footerLabel:  '12px',    // footer column headings
  footerLink:   '15px',    // footer nav links
  footerBody:   '0.93rem', // footer body / tagline text
  mobileNav:    '17px',    // mobile menu top-level
  mobileItem:   '15px',    // mobile menu sub-items
};

// ── CSS VARIABLE INJECTION ───────────────────────────────────
// Call from App.js inside useEffect when market changes.
// index.css uses var(--ds-*) for CSS-class-level styles.
export const injectCSSVars = (market) => {
  const isMWO = market === 'mwo';
  const root = document.documentElement;

  // Brand fonts
  root.style.setProperty('--ds-font-display', isMWO
    ? "'Barlow Condensed', sans-serif"
    : "'Libre Baskerville', Georgia, serif");
  root.style.setProperty('--ds-font-body', isMWO
    ? "'Barlow', sans-serif"
    : "'Nunito Sans', 'Helvetica Neue', sans-serif");

  // Accent colour
  root.style.setProperty('--ds-accent',       '#00BCD4');
  root.style.setProperty('--ds-accent-hover', '#0097A7');

  // Nav
  root.style.setProperty('--ds-fz-nav',      uiScale.nav);
  root.style.setProperty('--ds-fz-dropdown', uiScale.dropdown);
  root.style.setProperty('--ds-nav-color',   isMWO ? 'rgba(232,228,220,0.65)' : 'rgba(255,255,255,0.85)');
  root.style.setProperty('--ds-dropdown-bg', isMWO ? '#161920' : '#152266');

  // Buttons
  root.style.setProperty('--ds-fz-btn',      uiScale.btn);
  root.style.setProperty('--ds-fz-btn-mwo',  uiScale.btnMwo);

  // Eyebrow
  root.style.setProperty('--ds-fz-eyebrow', uiScale.eyebrow);

  // Footer
  root.style.setProperty('--ds-fz-footer-label', uiScale.footerLabel);
  root.style.setProperty('--ds-fz-footer-link',  uiScale.footerLink);
  root.style.setProperty('--ds-fz-footer-body',  uiScale.footerBody);

  // Mobile nav
  root.style.setProperty('--ds-fz-mobile-nav',  uiScale.mobileNav);
  root.style.setProperty('--ds-fz-mobile-item', uiScale.mobileItem);
};

// ── BRAND TOKENS ─────────────────────────────────────────────
// Used directly in page/component inline styles via:
//   const b = getBrand(market);
export const getBrand = (market) => {
  const isMWO = market === 'mwo';

  return {
    isMWO,
    market,

    /* ── Typography ── */
    fontDisplay: isMWO
      ? "'Barlow Condensed', sans-serif"
      : "'Libre Baskerville', Georgia, serif",
    fontBody: isMWO
      ? "'Barlow', sans-serif"
      : "'Nunito Sans', 'Helvetica Neue', sans-serif",

    // Type scale — change typeScale above to update everywhere
    fz: typeScale,

    /* ── Heading sizes ── */
    h2size: 'clamp(1.9rem, 4vw, 2.8rem)',
    h3size: typeScale.lg,   // keeps card headings in sync with fz.lg

    /* ── Colours ── */
    bg:        isMWO ? '#0D0F14'  : '#ffffff',
    bgAlt:     isMWO ? '#111318'  : '#F4F6FB',
    bgCard:    isMWO ? '#161920'  : '#ffffff',
    bgLight:   isMWO ? '#E8E4DC' : '#F4F6FB',
    bgCTA:     isMWO ? '#111318'  : '#0F1B45',

    // WCAG AA on dark MWO bg (#0D0F14):
    //   #E8E4DC @ 0.74 ≈ 7.0:1  (passes AAA)
    //   was 0.58 ≈ 5.0:1 (barely passed AA, hard to read)
    text:        isMWO ? '#E8E4DC'                : '#1e2847',
    textMuted:   isMWO ? 'rgba(232,228,220,0.74)' : '#64748b',
    textHeading: isMWO ? '#E8E4DC'                : '#0F1B45',
    textOnDark:  '#ffffff',

    accent:  '#00BCD4',
    navy:    '#1A237E',

    border:     isMWO ? 'rgba(255,255,255,0.08)' : 'rgba(15,27,69,0.09)',
    borderCard: isMWO ? 'rgba(255,255,255,0.07)' : 'transparent',

    /* ── Hero ── */
    heroBg: isMWO
      ? '#0D0F14'
      : 'linear-gradient(155deg, #F4F6FB 0%, #E8EDF8 100%)',

    /* ── Cards ── */
    cardRadius:    isMWO ? '0'    : '12px',
    cardShadow:    isMWO ? 'none' : '0 2px 16px rgba(15,27,69,0.08)',
    cardTopBorder: isMWO ? '3px solid #00BCD4' : 'none',

    /* ── Eyebrow label ── */
    eyebrowColor: isMWO ? '#00BCD4' : '#0097A7',

    /* ── Checklist ── */
    checklistBg:   isMWO ? '#161920' : '#F0F7FF',
    checklistText: isMWO ? '#E8E4DC' : '#1e2847',

    /* ── Process steps ── */
    stepBg:     isMWO ? 'transparent' : '#00BCD4',
    stepBorder: isMWO ? '2px solid #00BCD4' : 'none',
    stepColor:  isMWO ? '#00BCD4' : '#ffffff',

    /* ── CTA / Buttons ── */
    ctaBtnClass: isMWO ? 'btn-primary-mwo' : 'btn-primary-mindit',

    /* ── Section padding ── */
    secPad:  'clamp(64px, 8vw, 100px) clamp(24px, 6vw, 80px)',
    heroPad: 'clamp(88px, 11vw, 144px) clamp(24px, 6vw, 80px)',
  };
};
